import { useT } from '@hana/i18n';
import '../i18n';
import { useMemo, useState, type TouchEvent } from 'react';
import {
  IconAlarm, IconArchive, IconArrowBackUp, IconArrowBackUpDouble, IconArrowForwardUp, IconBell, IconBuilding, IconCheck, IconChevronDown, IconChevronLeft,
  IconDots, IconDownload, IconExternalLink, IconFlag, IconFolder, IconFolderPlus, IconLayoutGrid, IconLayoutSidebarRight, IconMail, IconMailOpened, IconMessages,
  IconMoon, IconPencil, IconPlus, IconRefresh, IconSearch, IconSettings, IconSun, IconTag, IconTrash, IconUserPlus,
} from '@tabler/icons-react';
import { AppIcon, useTheme, type ScreenState } from '@hana/ui';
import { ATT_MB, MAILS, unreadCount, type BoxMail } from '../data';
import { attTotal, boxCounts, preview, boxTitle, filterMails, groupByDay, initials, inboxOf, slaLabel, slaShort, UNASSIGNED } from './view';
import s from './compact.module.css';

// Mint-Box-iPad (1180) + Mint-Box-Phone (390): Box là app cài riêng, có rail / thanh tab riêng, không dùng menu ERP (board: "RAIL RIÊNG CỦA HANA BOX").
// Chỉ đọc, dữ liệu mẫu: nút gửi / lưu / giao PIC báo toast "Chưa mở … ở bản này" (docs/streams/I.md).

export type Soon = () => void;

type Chip = 'open' | 'unassigned' | 'late' | 'mine' | 'done';

const CHIPS: ReadonlyArray<{ key: Chip; label: string }> = [
  { key: 'open', label: 'Chưa xử lý' },
  { key: 'unassigned', label: 'Chưa gán' },
  { key: 'late', label: 'Quá SLA' },
  { key: 'mine', label: 'Của tôi' },
  { key: 'done', label: 'Đã xử lý' },
];

function chipList(mails: readonly BoxMail[], c: Chip): BoxMail[] {
  if (c === 'unassigned') return filterMails(mails, 'all').filter((m) => m.pic === UNASSIGNED);
  if (c === 'late') return filterMails(mails, 'all').filter((m) => (m.sla ?? 0) < 0);
  if (c === 'done') return mails.filter((m) => m.done);
  if (c === 'mine') return filterMails(mails, 'mine');
  return filterMails(mails, 'all');
}

function useInbox(q: string, chip: Chip) {
  const inbox = useMemo(() => inboxOf('info'), []);
  const counts = useMemo(() => boxCounts('info'), []);
  const list = useMemo(() => {
    const byChip = chipList(inbox, chip);
    const needle = q.trim().toLowerCase();
    return needle ? byChip.filter((m) => `${m.from} ${m.subj} ${m.job} ${m.bk} ${m.kh}`.toLowerCase().includes(needle)) : byChip;
  }, [inbox, chip, q]);
  return { counts, list, inbox };
}

const chipN = (c: ReturnType<typeof boxCounts>, k: Chip): number =>
  k === 'open' ? c.smart.open : k === 'unassigned' ? c.smart.unassigned : k === 'late' ? c.smart.late : k === 'mine' ? c.smart.mine : c.smart.done;

function Avatar({ pic }: { pic: string }) {
  return <span className={s.ava} role="img" aria-label={`PIC ${pic}`}>{initials(pic)}</span>;
}

function Meta({ m }: { m: BoxMail }) {
  const t = useT();
  const sla = slaShort(m);
  return (
    <span className={s.meta}>
      {m.label && (
        <span className={`${s.tag} ${m.k ? s.tagErr : ''}`}>
          {!m.k && <IconTag size={12} stroke={1.9} aria-hidden="true" />}
          {m.label}
        </span>
      )}
      {m.pic === UNASSIGNED && <span className={s.unassigned}><IconUserPlus size={12} stroke={1.9} aria-hidden="true" />{t('box.unassigned')}</span>}
      {m.job && <span className={s.jobChip}><IconExternalLink size={12} stroke={1.9} aria-hidden="true" />{m.job}</span>}
      {m.kh && <span className={s.kh}><IconBuilding size={12} stroke={1.9} aria-hidden="true" />{m.kh}</span>}
      {sla && (
        <span className={`${s.sla} ${(m.sla ?? 0) < 0 ? s.slaLate : (m.sla ?? 99) <= 5 ? s.slaSoon : ''}`} role="img" aria-label={`SLA ${slaLabel(m)}`}>
          <IconAlarm size={12} stroke={1.9} aria-hidden="true" />
          {sla}
        </span>
      )}
    </span>
  );
}

function RowBody({ m }: { m: BoxMail }) {
  return (
    <>
      <span className={s.dotCol}>{m.unread ? <span className={s.unread} /> : null}</span>
      <span className={s.rowBody}>
        <span className={s.rowTop}>
          <span className={s.from}>{m.from}</span>
          <span className={s.time}>{m.time}</span>
        </span>
        <span className={`${s.subj} ${m.unread ? s.subjB : ''}`}>{m.subj}</span>
        <span className={s.snip}>{preview(m)}</span>
        <Meta m={m} />
      </span>
    </>
  );
}

function Reader({ m, soon, pad, onInfo }: { m: BoxMail; soon: Soon; pad: boolean; onInfo?: () => void }) {
  const t = useT();
  const sla = slaShort(m);
  const old = Math.max(0, m.thread - 1);
  return (
    <>
      {pad && (
        <div className={s.rHead}>
          <h2 className={s.rSubj}>{m.subj}</h2>
          <button type="button" className={s.sqBtn} aria-label="Thông tin job / khách" onClick={onInfo ?? soon}><IconLayoutSidebarRight size={18} stroke={1.9} aria-hidden="true" /></button>
        </div>
      )}
      {pad && <div className={s.rFrom}><b>{m.from}</b> · 22/Oct/2026 {m.time}</div>}
      {pad && (
        <div className={s.acts} role="toolbar" aria-label="Thao tác thư">
          <button type="button" className={s.pic} aria-label={`PIC ${m.pic} · giao PIC`} onClick={soon}>
            <Avatar pic={m.pic === UNASSIGNED ? 'Ch' : m.pic} />{m.pic === UNASSIGNED ? 'Chưa gán' : m.pic}<IconChevronDown size={12} stroke={2} aria-hidden="true" />
          </button>
          <button type="button" className={`${s.act} ${s.actOn}`} aria-label="Trả lời" onClick={soon}><IconArrowBackUp size={17} stroke={1.9} aria-hidden="true" /></button>
          <button type="button" className={s.act} aria-label="Trả lời tất cả" onClick={soon}><IconArrowBackUpDouble size={17} stroke={1.9} aria-hidden="true" /></button>
          <button type="button" className={s.act} aria-label="Chuyển tiếp" onClick={soon}><IconArrowForwardUp size={17} stroke={1.9} aria-hidden="true" /></button>
          <button type="button" className={s.act} aria-label="Follow Up +2 giờ" onClick={soon}><IconAlarm size={17} stroke={1.9} aria-hidden="true" /></button>
          <button type="button" className={s.act} aria-label="Đã xử lý" onClick={soon}><IconCheck size={17} stroke={1.9} aria-hidden="true" /></button>
          <button type="button" className={`${s.act} ${s.actS}`} aria-label="Lưu trữ" onClick={soon}><IconArchive size={17} stroke={1.9} aria-hidden="true" /></button>
          <button type="button" className={`${s.act} ${s.actS}`} aria-label="Di chuyển" onClick={soon}><IconFolder size={17} stroke={1.9} aria-hidden="true" /></button>
          <button type="button" className={`${s.act} ${s.actS}`} aria-label="Gắn cờ" onClick={soon}><IconFlag size={17} stroke={1.9} aria-hidden="true" /></button>
          <button type="button" className={`${s.act} ${s.actS}`} aria-label="Chưa đọc" onClick={soon}><IconMailOpened size={17} stroke={1.9} aria-hidden="true" /></button>
          <button type="button" className={`${s.act} ${s.actS} ${s.actDel}`} aria-label="Xoá" onClick={soon}><IconTrash size={17} stroke={1.9} aria-hidden="true" /></button>
          <button type="button" className={`${s.act} ${s.actS}`} aria-label="Thêm" onClick={soon}><IconDots size={17} stroke={1.9} aria-hidden="true" /></button>
        </div>
      )}
      <div className={s.tags}>
        {m.label && <span className={s.tagL}><IconTag size={13} stroke={1.9} aria-hidden="true" />{m.label}</span>}
        {m.kh && <span className={s.tagK}><IconBuilding size={13} stroke={1.9} aria-hidden="true" />{m.kh}</span>}
        <a className={s.tagQ} href="/hei/"><IconMessages size={14} stroke={1.9} aria-hidden="true" />{t('box.askTeam')}</a>
        {m.job && <a className={s.tagJob} href={`/ezy/jobs/${m.job}`} aria-label={`Mở job ${m.job} ở HANA Ezy`}><span>{m.job}</span><IconExternalLink size={13} stroke={1.9} aria-hidden="true" /></a>}
        {sla && <span className={s.tagSla} role="img" aria-label={`SLA ${slaLabel(m)}`}><IconAlarm size={13} stroke={1.9} aria-hidden="true" />{sla}</span>}
      </div>
      {m.att.length > 0 && (
        <div className={s.atts} role="group" aria-label="Tệp đính kèm">
          {m.att.map((a) => (
            <div key={a.name} className={s.att}>
              <a className={s.attMain} href="#" aria-label={`Xem ${a.name}`} onClick={(e) => { e.preventDefault(); soon(); }}>
                <span className={s.pdf}>{a.name.endsWith('.pdf') ? 'PDF' : 'XLS'}</span>
                <span className={s.attName}>{a.name}</span>
                <span className={s.attKb}>{Math.round((ATT_MB[a.name] ?? 0.1) * 1000)} KB</span>
              </a>
              <button type="button" className={s.attMore} aria-label={`Thao tác với ${a.name}`} onClick={soon}><IconDots size={16} stroke={1.9} aria-hidden="true" /></button>
            </div>
          ))}
          <span className={s.attTot}>{m.att.length} tệp · {attTotal(m, ATT_MB)}</span>
          <button type="button" className={s.attBtn} onClick={soon}><IconDownload size={14} stroke={1.9} aria-hidden="true" />{t('box.downloadAll')}</button>
          <button type="button" className={`${s.attBtn} ${s.attSave}`} onClick={soon}><IconFolderPlus size={14} stroke={1.9} aria-hidden="true" />{t('box.saveAllToJob')}</button>
        </div>
      )}
      <div className={s.sum}>
        <b>{t('box.askSummary')}</b>
        <ul>{m.sum.map((x) => <li key={x}>{x}</li>)}</ul>
        <div className={s.sumActs}>
          <button type="button" className={s.sumBtn} onClick={soon}><IconPencil size={14} stroke={1.9} aria-hidden="true" />{t('box.compose')}</button>
          <button type="button" className={s.sumBtn} onClick={soon}><IconPlus size={14} stroke={1.9} aria-hidden="true" />{t('box.createTask')}</button>
        </div>
      </div>
      {old > 0 && (
        <button type="button" className={s.older} onClick={soon}>
          <IconMail size={15} stroke={1.9} aria-hidden="true" /><span>{old} thư cũ hơn{pad ? ' trong hội thoại' : ' ·'}</span><b>{t('box.openAll')}</b>
        </button>
      )}
      <div className={s.body}>
        {!pad && <div className={s.bFrom}><b>{m.from}</b> · 22/Oct/2026 {m.time}</div>}
        {m.body.map((p) => <p key={p}>{p}</p>)}
      </div>
    </>
  );
}

function SkRows({ n, card }: { n: number; card?: boolean }) {
  return (
    <>
      {Array.from({ length: n }, (_, k) => (
        <div key={k} className={card ? s.skCard : s.skRow} aria-hidden="true"><i style={{ width: card ? '50%' : '45%' }} /><i style={{ width: card ? '90%' : '85%' }} /></div>
      ))}
    </>
  );
}

function EmptyList({ big }: { big?: boolean }) {
  const t = useT();
  return (
    <div className={`${s.stBody} ${big ? s.stBig : ''}`}>
      <div className={`${s.stIc} ${big ? s.stIcBig : ''}`}><IconMail size={big ? 28 : 26} stroke={1.8} aria-hidden="true" /></div>
      <b>{t('box.st.doneAll')}</b>
    </div>
  );
}

function ErrBlock({ desc, big, soon }: { desc: string; big?: boolean; soon: Soon }) {
  const t = useT();
  return (
    <div className={`${s.stBody} ${big ? s.stBig : s.stFill}`}>
      <div className={`${s.stIc} ${s.stErr} ${big ? s.stIcBig : ''}`}>!</div>
      <b>{t('box.st.errTitle')}</b>
      <span className={s.stMax}>{desc}</span>
      <button type="button" className={`${s.stBtn} ${big ? s.stBtnBig : ''}`} onClick={soon}><IconRefresh size={16} stroke={1.8} aria-hidden="true" />{t('box.retry')}</button>
    </div>
  );
}

export function BoxPad({ state, soon }: { state: ScreenState; soon: Soon }) {
  const t = useT();
  const { theme, toggle } = useTheme();
  const [chip, setChip] = useState<Chip>('open');
  const [q, setQ] = useState('');
  const [sel, setSel] = useState(1);
  const { counts, list } = useInbox(q, chip);
  const groups = useMemo(() => groupByDay(list), [list]);
  const cur = list.find((m) => m.id === sel) ?? list[0];
  const unread = unreadCount(MAILS);
  return (
    <div className={`${s.root} ${s.pad}`}>
      <nav className={s.rail} aria-label="HANA Box">
        <button type="button" className={s.railApp} aria-label="Menu HANA Box: hộp thư, thư mục, nhãn, cài đặt" onClick={soon}><AppIcon app="box" size={44} /></button>
        <button type="button" className={`${s.railBtn} ${s.railOn}`} aria-label="Thư" aria-pressed="true" onClick={soon}>
          <IconMail size={21} stroke={1.9} aria-hidden="true" />{t('box.mail')}<span className={s.railN}>{unread}</span>
        </button>
        <button type="button" className={s.railBtn} aria-label="Tìm" onClick={soon}><IconSearch size={21} stroke={1.9} aria-hidden="true" />{t('box.search')}</button>
        <button type="button" className={s.railBtn} aria-label="Thông báo" onClick={soon}>
          <IconBell size={21} stroke={1.9} aria-hidden="true" />{t('box.notifications')}<span className={s.railN}>4</span>
        </button>
        <span className={s.grow} />
        <button type="button" className={s.railBtn} aria-label="Chuyển app" aria-haspopup="menu" onClick={() => location.assign('/')}><IconLayoutGrid size={20} stroke={2} aria-hidden="true" />{t('box.zone')}</button>
        <button type="button" className={s.railIc} aria-label="Cài đặt HANA Box" onClick={soon}><IconSettings size={20} stroke={1.9} aria-hidden="true" /></button>
        <button type="button" className={s.railIc} aria-label={theme === 'dark' ? 'Chuyển giao diện sáng' : 'Chuyển giao diện tối'} onClick={toggle}>
          {theme === 'dark' ? <IconSun size={20} stroke={1.9} aria-hidden="true" /> : <IconMoon size={20} stroke={1.9} aria-hidden="true" />}
        </button>
      </nav>
      {(
        <>
          <section className={s.c2} aria-label="Danh sách thư">
            <div className={s.top}>
              <h1 className={s.h1}>HANA Box</h1>
              <button type="button" className={s.compose} onClick={soon}><IconPencil size={16} stroke={1.9} aria-hidden="true" />{t('box.newMail')}</button>
            </div>
            <div className={s.pickRow}>
              <button type="button" className={s.box} aria-haspopup="menu" onClick={soon}>{boxTitle('info')}<span aria-hidden="true">▾</span></button>
              <label className={s.search}>
                <IconSearch size={16} stroke={2.2} aria-hidden="true" />
                <input aria-label="Tìm thư" placeholder="Tìm thư, JobID…" value={q} onChange={(e) => setQ(e.target.value)} />
              </label>
            </div>
            <div className={s.chips} role="group" aria-label="Lọc danh sách">
              {CHIPS.map((c) => (
                <button key={c.key} type="button" className={`${s.chip} ${chip === c.key ? s.chipOn : ''}`} aria-pressed={chip === c.key} onClick={() => setChip(c.key)}>
                  {c.label} <span>{chipN(counts, c.key)}</span>
                </button>
              ))}
            </div>
            <div className={s.rows}>
              {state === 'loading' && <SkRows n={7} />}
              {state === 'empty' && <EmptyList />}
              {state !== 'loading' && state !== 'empty' && groups.flatMap((g) => g.rows).map((m) => (
                <button key={m.id} type="button" className={`${s.row} ${cur?.id === m.id ? s.rowOn : ''}`} aria-current={cur?.id === m.id || undefined} onClick={() => setSel(m.id)}>
                  <RowBody m={m} />
                </button>
              ))}
              {list.length === 0 && state !== 'empty' && state !== 'loading' && <div className={s.none}>{t('box.noMatch')}</div>}
            </div>
          </section>
          <section className={s.c3} aria-label="Đọc thư">
            {state === 'error' ? <ErrBlock desc={t('box.st.errDescPad')} soon={soon} /> : state === 'loading' ? (
              <div className={s.skRead} aria-busy="true" aria-label={t('box.st.loading')}>
                <i style={{ width: '60%', height: 22 }} />{[1, 2, 3, 4, 5, 6, 7].map((k) => <i key={k} />)}
              </div>
            ) : state === 'empty' ? <div className={s.none}>{t('box.pickMail')}</div> : cur ? <Reader m={cur} soon={soon} pad /> : <div className={s.none}>{t('box.pickMail')}</div>}
          </section>
        </>
      )}
    </div>
  );
}

const SWIPE = 172;

export function BoxPhone({ state, soon }: { state: ScreenState; soon: Soon }) {
  const t = useT();
  const { theme, toggle } = useTheme();
  const [chip, setChip] = useState<Chip>('open');
  const [q] = useState('');
  const [open, setOpen] = useState<number | null>(null);
  const [swiped, setSwiped] = useState<number | null>(2);
  const [x0, setX0] = useState<number | null>(null);
  const { counts, list } = useInbox(q, chip);
  const groups = useMemo(() => groupByDay(list), [list]);
  const rows = groups.flatMap((g) => g.rows);
  const cur = rows.find((m) => m.id === open);
  const unread = unreadCount(MAILS);
  const bx = counts.boxes;

  const onEnd = (id: number) => (e: TouchEvent) => {
    const dx = (e.changedTouches[0]?.clientX ?? 0) - (x0 ?? 0);
    if (dx < -40) setSwiped(id);
    else if (dx > 40) setSwiped(null);
    setX0(null);
  };

  if (cur) {
    return (
      <div className={`${s.root} ${s.phone}`}>
        <header className={s.pHead}>
          <button type="button" className={s.sqBtn} aria-label="Quay lại danh sách" onClick={() => setOpen(null)}><IconChevronLeft size={20} stroke={2} aria-hidden="true" /></button>
          <span className={s.pWho}><b>{cur.from}</b><span>22/Oct/2026 {cur.time} · {cur.box}@</span></span>
          <button type="button" className={s.sqBtn} aria-label="Thao tác thư" onClick={soon}><IconDots size={18} stroke={2} aria-hidden="true" /></button>
          <button type="button" className={s.done} aria-label="Đánh dấu đã xử lý" onClick={soon}><IconCheck size={16} stroke={1.9} aria-hidden="true" />{t('box.done')}</button>
        </header>
        <div className={s.pRead}>
          <h1 className={s.pSubj}>{cur.subj}</h1>
          <Reader m={cur} soon={soon} pad={false} />
        </div>
        <div className={s.pBar}>
          <button type="button" className={`${s.pBtn} ${s.pBtnOn}`} onClick={soon}><IconArrowBackUp size={16} stroke={1.9} aria-hidden="true" />{t('box.reply')}</button>
          <button type="button" className={s.pBtn} onClick={soon}><IconArrowBackUpDouble size={16} stroke={1.9} aria-hidden="true" />{t('box.replyAll')}</button>
          <button type="button" className={s.pBtn} onClick={soon}><IconArrowForwardUp size={16} stroke={1.9} aria-hidden="true" />{t('box.forward')}</button>
        </div>
      </div>
    );
  }

  return (
    <div className={`${s.root} ${s.phone}`}>
      <div className={s.pTop}>
        <h1 className={s.h1}>HANA Box</h1>
        <button type="button" className={s.sqBtn} aria-label={theme === 'dark' ? 'Chuyển giao diện sáng' : 'Chuyển giao diện tối'} onClick={toggle}>
          {theme === 'dark' ? <IconSun size={19} stroke={1.9} aria-hidden="true" /> : <IconMoon size={19} stroke={1.9} aria-hidden="true" />}
        </button>
        <button type="button" className={`${s.sqBtn} ${s.sqDark}`} aria-label="Soạn thư" onClick={soon}><IconPencil size={19} stroke={1.9} aria-hidden="true" /></button>
      </div>
      {(
        <>
          <div className={s.pBoxes} role="group" aria-label="Hộp thư">
            <button type="button" className={`${s.chip} ${s.chipBox} ${s.chipBoxOn}`} onClick={soon}>info@ <span className={s.warnN}>{bx.info}</span></button>
            <button type="button" className={`${s.chip} ${s.chipBox}`} onClick={soon}>support@ <span>{bx.support}</span></button>
            <button type="button" className={`${s.chip} ${s.chipBox}`} onClick={soon}>{t('box.mine')} <span>{counts.mine}</span></button>
            <button type="button" className={`${s.chip} ${s.chipBox}`} onClick={soon}>booking@ <span>{bx.booking}</span></button>
            <button type="button" className={`${s.chip} ${s.chipBox}`} onClick={soon}>trucking@ <span>{bx.trucking}</span></button>
            <button type="button" className={`${s.chip} ${s.chipBox}`} onClick={soon}>billing@ <span>{bx.billing}</span></button>
          </div>
          <div className={s.chips} role="group" aria-label="Lọc danh sách">
            {CHIPS.map((c) => (
              <button key={c.key} type="button" className={`${s.chip} ${chip === c.key ? s.chipOn : ''}`} aria-pressed={chip === c.key} onClick={() => setChip(c.key)}>
                {c.label} <span>{chipN(counts, c.key)}</span>
              </button>
            ))}
          </div>
          <div className={s.pRows}>
            {state === 'loading' && <SkRows n={6} card />}
            {state === 'empty' && <EmptyList big />}
            {state === 'error' && <ErrBlock big desc={t('box.st.errDescPhone')} soon={soon} />}
            {state !== 'loading' && state !== 'empty' && state !== 'error' && rows.map((m) => (
              <div key={m.id} className={s.swipe}>
                <div className={s.swActs}>
                  <button type="button" className={s.swPic} onClick={soon}><IconUserPlus size={15} stroke={1.9} aria-hidden="true" />{t('box.assignPic')}</button>
                  <button type="button" className={s.swDone} onClick={soon}><IconCheck size={15} stroke={1.9} aria-hidden="true" />{t('box.done')}</button>
                </div>
                <button
                  type="button"
                  className={`${s.row} ${s.pRow} ${m.id === 1 ? s.rowOn : ''}`}
                  aria-label={`${m.from} · ${m.subj}`}
                  style={{ transform: swiped === m.id ? `translateX(-${SWIPE}px)` : undefined }}
                  onTouchStart={(e) => setX0(e.touches[0]?.clientX ?? 0)}
                  onTouchEnd={onEnd(m.id)}
                  onClick={() => (swiped === m.id ? setSwiped(null) : setOpen(m.id))}
                >
                  <RowBody m={m} />
                </button>
              </div>
            ))}
            {rows.length === 0 && state === 'ready' && <div className={s.none}>{t('box.noMatch')}</div>}
          </div>
        </>
      )}
      <nav className={s.pTabs} aria-label="Điều hướng HANA Box">
        <button type="button" className={`${s.pTab} ${s.pTabOn}`} aria-current="page" onClick={soon}><IconMail size={22} stroke={1.9} aria-hidden="true" />{t('box.mail')} <span className={s.tabN}>{unread}</span></button>
        <button type="button" className={s.pTab} onClick={soon}><IconSearch size={22} stroke={1.9} aria-hidden="true" />{t('box.search')}</button>
        <button type="button" className={s.pTab} onClick={soon}><IconBell size={22} stroke={1.9} aria-hidden="true" />{t('box.notifications')} <span className={s.tabN}>4</span></button>
        <button type="button" className={s.pTab} aria-haspopup="menu" onClick={() => location.assign('/')}><IconDots size={22} stroke={1.9} aria-hidden="true" />{t('box.more')}</button>
      </nav>
    </div>
  );
}
